import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import { DsButton, DsModal, DsSearchInput } from '@/modules/internal-v2/ds';
import {
  useDeleteContactList,
  useUpdateContactList,
} from '@/api/contact-list/contact-list.hooks';
import type {
  ContactListKind,
  ContactListMember,
} from '@/api/contact-list/contact-list.types';
import { DsTable } from '../../shared/ds-table.component';
import type { VerteilerListContext } from '../verteiler.page';
import { ContactListModal } from './contact-list-modal.component';
import { VerteilerAddContacts } from './verteiler-add-contacts.component';
import { VerteilerImportModal } from './verteiler-import-modal.component';

const KIND_LABELS: Record<ContactListKind, string> = {
  customer: 'Kunden',
  supplier: 'Lieferanten',
};

/** Placeholder shown at `/i/business-partners/verteiler` (no list selected). */
export function VerteilerDetailEmpty() {
  return (
    <div className="bp-empty">
      <div>
        <div className="big">Kein Verteiler ausgewählt</div>
        <div>Wähle einen Verteiler aus der Liste — oder lege einen neuen an.</div>
      </div>
    </div>
  );
}

const col = createColumnHelper<ContactListMember>();

function matchesMember(member: ContactListMember, term: string): boolean {
  return (
    member.name.toLowerCase().includes(term) ||
    member.businessPartnerName.toLowerCase().includes(term) ||
    (member.email?.toLowerCase().includes(term) ?? false) ||
    (member.position?.toLowerCase().includes(term) ?? false)
  );
}

/**
 * Detail panel for the Verteiler addressed by the `:listId` route param.
 * Manages membership in place: a sortable, filterable members table, with an
 * add mode that swaps in the company-wide contact directory.
 */
export function VerteilerDetail() {
  const { listId } = useParams<{ listId: string }>();
  const { lists, listsPending, listsFetching } =
    useOutletContext<VerteilerListContext>();
  const navigate = useNavigate();

  const id = Number(listId);
  const list = lists.find((entry) => entry.id === id);

  const [query, setQuery] = useState('');
  const [sorting, setSorting] = useState<SortingState>([
    { id: 'name', desc: false },
  ]);
  const [mode, setMode] = useState<'members' | 'add'>('members');
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [importing, setImporting] = useState(false);

  // Reset transient view state when the selected Verteiler changes.
  const [syncedId, setSyncedId] = useState(listId);
  if (syncedId !== listId) {
    setSyncedId(listId);
    setQuery('');
    setMode('members');
    setEditing(false);
    setConfirmDelete(false);
    setImporting(false);
  }

  const { mutate: updateList, isPending: isUpdating } = useUpdateContactList();
  const deleteMutation = useDeleteContactList();

  const members = useMemo(() => list?.members ?? [], [list]);

  const filteredMembers = useMemo(() => {
    const term = query.trim().toLowerCase();
    return term ? members.filter((m) => matchesMember(m, term)) : members;
  }, [members, query]);

  const handleRemove = useCallback(
    (memberId: number) => {
      if (!list) return;
      const nextIds = list.members
        .map((m) => m.contactEmployeeId)
        .filter((mid) => mid !== memberId);
      updateList({ listId: list.id, body: { contactEmployeeIds: nextIds } });
    },
    [list, updateList],
  );

  const handleAdd = useCallback(
    (idsToAdd: number[]) => {
      if (!list || idsToAdd.length === 0) return;
      const nextIds = [
        ...new Set([
          ...list.members.map((m) => m.contactEmployeeId),
          ...idsToAdd,
        ]),
      ];
      updateList(
        { listId: list.id, body: { contactEmployeeIds: nextIds } },
        { onSuccess: () => setMode('members') },
      );
    },
    [list, updateList],
  );

  const columns = useMemo(
    () => [
      col.accessor('name', {
        header: 'Name',
        meta: { flex: true },
        sortingFn: 'text',
        cell: (info) => (
          <span style={{ fontWeight: 500, color: 'var(--ink)' }}>
            {info.getValue()}
          </span>
        ),
      }),
      col.accessor('businessPartnerName', {
        header: 'Firma',
        size: 200,
        sortingFn: 'text',
      }),
      col.accessor('position', {
        header: 'Position',
        size: 150,
        cell: (info) => info.getValue() || '—',
      }),
      col.accessor('email', {
        header: 'E-Mail',
        size: 230,
        cell: (info) => info.getValue() || '—',
      }),
      col.display({
        id: 'remove',
        header: '',
        size: 96,
        cell: (info) => (
          <button
            type="button"
            className="vt-remove-btn"
            disabled={isUpdating}
            onClick={() => handleRemove(info.row.original.contactEmployeeId)}
          >
            Entfernen
          </button>
        ),
      }),
    ],
    [handleRemove, isUpdating],
  );

  const table = useReactTable({
    data: filteredMembers,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getRowId: (row) => String(row.contactEmployeeId),
    enableSortingRemoval: false,
  });

  if (!list) {
    const loading = listsPending || listsFetching;
    return (
      <div className="bp-empty">
        <div>
          <div className="big">
            {loading ? 'Laden…' : 'Verteiler nicht gefunden'}
          </div>
          {!loading && <div>Wähle einen Verteiler aus der Liste.</div>}
        </div>
      </div>
    );
  }

  const handleConfirmDelete = () => {
    deleteMutation.mutate(list.id, {
      onSuccess: () => navigate('/i/business-partners/verteiler'),
    });
  };

  return (
    <div className="vt-detail">
      <div className="vt-detail-header">
        <div className="vt-detail-title-row">
          <span className="vt-detail-name">{list.name}</span>
          <span className="vt-kind-chip" data-kind={list.kind}>
            {KIND_LABELS[list.kind]}
          </span>
          <span className="vt-detail-count">
            {members.length} {members.length === 1 ? 'Kontakt' : 'Kontakte'}
          </span>
          <div className="vt-detail-actions">
            <DsButton size="small" onClick={() => setEditing(true)}>
              Bearbeiten
            </DsButton>
            <DsButton
              size="small"
              variant="ghost"
              onClick={() => setConfirmDelete(true)}
            >
              Löschen
            </DsButton>
          </div>
        </div>
        {list.description && (
          <div className="vt-detail-desc">{list.description}</div>
        )}
      </div>

      {mode === 'add' ? (
        <VerteilerAddContacts
          kind={list.kind}
          memberIds={members.map((m) => m.contactEmployeeId)}
          saving={isUpdating}
          onSubmit={handleAdd}
          onClose={() => setMode('members')}
        />
      ) : (
        <>
          <div className="vt-detail-toolbar">
            <DsSearchInput
              className="vt-detail-search"
              value={query}
              onSearch={setQuery}
              debounce={0}
              placeholder="Mitglieder filtern…"
            />
            <div className="vt-toolbar-end">
              <DsButton
                size="small"
                variant="ghost"
                onClick={() => setImporting(true)}
                disabled={isUpdating}
                title="Standardkontakte oder alle Kontakte (optional nur mit Mailempfang-Einwilligung) in einem Schritt hinzufügen"
              >
                Kontakte importieren
              </DsButton>
              <DsButton
                size="small"
                variant="primary"
                onClick={() => setMode('add')}
              >
                + Ansprechpartner hinzufügen
              </DsButton>
            </div>
          </div>
          <div className="vt-detail-body">
            <DsTable
              table={table}
              fixedLayout
              emptyMessage={
                members.length === 0
                  ? 'Noch keine Ansprechpartner — füge welche hinzu.'
                  : 'Keine Treffer.'
              }
            />
          </div>
        </>
      )}

      {importing && (
        <VerteilerImportModal
          listId={list.id}
          kind={list.kind}
          onClose={() => setImporting(false)}
        />
      )}

      {editing && (
        <ContactListModal list={list} onClose={() => setEditing(false)} />
      )}

      {confirmDelete && (
        <DsModal
          open
          onClose={() => setConfirmDelete(false)}
          title="Verteiler löschen"
          description={`„${list.name}“ wird dauerhaft entfernt.`}
          footer={
            <>
              <DsButton
                variant="ghost"
                onClick={() => setConfirmDelete(false)}
                disabled={deleteMutation.isPending}
              >
                Abbrechen
              </DsButton>
              <DsButton
                variant="primary"
                onClick={handleConfirmDelete}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? 'Löschen…' : 'Löschen'}
              </DsButton>
            </>
          }
        >
          <p
            style={{
              margin: 0,
              fontSize: 'var(--fs-sm)',
              color: 'var(--ink-2)',
            }}
          >
            Diese Aktion kann nicht rückgängig gemacht werden.
          </p>
        </DsModal>
      )}
    </div>
  );
}
